/**
 * useMicRecorder — PUSH-TO-TALK mic capture state-machine hook.
 *
 * Responsibilities:
 *   - Feature-detect MediaRecorder + getUserMedia at mount.
 *   - start(): idle→request mic→record (call on pointer-DOWN; records a press timestamp).
 *   - stop():  recording→stop→transcribe→onTranscript (call on pointer-UP/leave/cancel).
 *   - Push-to-talk guard: a press shorter than `minHoldMs` (default 450 ms), an
 *     effectively-empty blob, OR a release before getUserMedia resolves does NOT
 *     transcribe — instead it surfaces a friendly `hint` ("halt den Mic-Button
 *     gedrückt …") and returns to idle. It is a hint, NOT an error (no MicOff).
 *   - cancel(): discard in-flight recording or transcription; no onTranscript call.
 *   - Graceful denial (NotAllowedError): state='denied', no throw.
 *   - Cleanup on unmount: release OS mic indicator (track.stop()), abort in-flight transcribe.
 *
 * The hook NEVER auto-sends. onTranscript drops the text into the caller's textarea.
 * All writes still traverse /agent/chat → awaiting_confirmation → /agent/confirm.
 *
 * [Source: Story P3b spec §4]
 */

/** Below this hold duration a press is treated as an accidental tap → the PTT hint. */
export const PTT_MIN_HOLD_MS = 450;

/** Friendly push-to-talk hint (a nudge, not an error). */
export const PTT_HINT = 'Das ist Push-to-Talk: halte den Mic-Button gedrückt und sprich.';

import { useRef, useState, useCallback, useEffect } from 'react';
import { agentTranscribe } from '../../../lib/api-client';
import type { AgentTranscribeResponseDto } from '@brokly/shared';

// ─── Public types ─────────────────────────────────────────────────────────────

export type MicState =
  | 'unsupported'  // feature-detect failed → caller hides the button
  | 'idle'
  | 'requesting'   // awaiting getUserMedia permission
  | 'recording'
  | 'transcribing' // POST /agent/transcribe in flight
  | 'denied'       // NotAllowedError / PermissionDenied
  | 'error';       // any other failure (no MediaRecorder, network, empty audio)

export interface UseMicRecorder {
  state: MicState;
  supported: boolean;
  error: string | null;
  /** Push-to-talk hint (accidental tap / empty capture / released too early). Not an error. */
  hint: string | null;
  /** Begin a press (pointer-DOWN): request the mic + start recording. */
  start: () => void;
  /** End a press (pointer-UP/leave/cancel): stop + transcribe on a valid hold, else hint. */
  stop: () => void;
  /** Discard recording without transcribing (e.g. unmount, ESC). */
  cancel: () => void;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useMicRecorder(opts: {
  onTranscript: (text: string) => void;
  /** Default 'de' — matches WHISPER_STT_MODEL_ID default. */
  language?: string;
  /** Minimum hold (ms) before a press counts as real speech. Default 450. Injectable for tests. */
  minHoldMs?: number;
  /** Injectable for tests; defaults to the real agentTranscribe. */
  transcribe?: typeof agentTranscribe;
}): UseMicRecorder {
  const { language = 'de', minHoldMs = PTT_MIN_HOLD_MS } = opts;

  // Keep latest callbacks in refs to avoid stale closures (avoids re-creating toggle on every render)
  const onTranscriptRef = useRef(opts.onTranscript);
  const transcribeRef = useRef(opts.transcribe ?? agentTranscribe);
  const languageRef = useRef(language);

  useEffect(() => {
    onTranscriptRef.current = opts.onTranscript;
  });
  useEffect(() => {
    transcribeRef.current = opts.transcribe ?? agentTranscribe;
  });
  useEffect(() => {
    languageRef.current = language;
  });
  const minHoldRef = useRef(minHoldMs);
  useEffect(() => {
    minHoldRef.current = minHoldMs;
  });

  // Feature detection — runs once at mount
  const supportedRef = useRef(
    typeof navigator !== 'undefined' &&
      !!navigator.mediaDevices?.getUserMedia &&
      typeof window.MediaRecorder !== 'undefined',
  );

  // Dual: ref for synchronous reads inside callbacks + state for React re-renders
  const stateRef = useRef<MicState>(supportedRef.current ? 'idle' : 'unsupported');
  const [state, _setStateRaw] = useState<MicState>(stateRef.current);
  const [error, setError] = useState<string | null>(null);
  const [hint, setHint] = useState<string | null>(null);

  const setState = useCallback((s: MicState) => {
    stateRef.current = s;
    _setStateRaw(s);
  }, []);

  // Non-state refs (not render data)
  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const abortRef = useRef<AbortController | null>(null);
  // Push-to-talk timing: when the press began, and whether the release was too quick.
  const pressStartRef = useRef<number>(0);
  const shortReleaseRef = useRef<boolean>(false);

  // ── Helpers ──────────────────────────────────────────────────────────────────

  const stopTracks = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  }, []);

  // ── cancel ────────────────────────────────────────────────────────────────────

  const cancel = useCallback(() => {
    // 1. Abort any in-flight transcribe (also signals onstop to skip transcription)
    abortRef.current?.abort();
    abortRef.current = null;
    // 2. Stop recorder if active — onstop will fire but will see signal.aborted = true
    const rec = recorderRef.current;
    if (rec && rec.state === 'recording') {
      rec.stop();
    }
    // 3. Stop OS mic indicator
    stopTracks();
    chunksRef.current = [];
    recorderRef.current = null;
    setState('idle');
    setError(null);
    setHint(null);
  }, [setState, stopTracks]);

  // ── start (pointer-DOWN) ────────────────────────────────────────────────────────

  const start = useCallback(() => {
    const current = stateRef.current;

    // Only a fresh press starts a recording; ignore while already busy.
    if (
      current === 'unsupported' ||
      current === 'requesting' ||
      current === 'recording' ||
      current === 'transcribing'
    ) {
      return;
    }

    // idle / denied / error → request the mic + begin recording.
    setError(null);
    setHint(null);
    shortReleaseRef.current = false;
    pressStartRef.current = Date.now();
    setState('requesting');

    // Create the AbortController now so onstop can see it via closure
    const currentAbort = new AbortController();
    abortRef.current = currentAbort;

    navigator.mediaDevices
      .getUserMedia({ audio: true })
      .then((stream) => {
        // Guard: stop()/cancel() may have fired while we awaited getUserMedia. A stop()
        // during 'requesting' already surfaced the PTT hint (released before ready).
        if (stateRef.current !== 'requesting') {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }

        streamRef.current = stream;
        chunksRef.current = [];

        // Select the best supported mimeType
        const mimeType = ['audio/webm', 'audio/mp4'].find((m) =>
          MediaRecorder.isTypeSupported(m),
        );
        const recorderOpts: MediaRecorderOptions = mimeType ? { mimeType } : {};
        const recorder = new MediaRecorder(stream, recorderOpts);
        recorderRef.current = recorder;

        // Collect chunks
        recorder.addEventListener('dataavailable', (e: BlobEvent) => {
          if (e.data && e.data.size > 0) {
            chunksRef.current.push(e.data);
          }
        });

        recorder.onstop = () => {
          // Release OS mic indicator
          stopTracks();

          const blob = new Blob(chunksRef.current, {
            type: recorder.mimeType || 'audio/webm',
          });
          chunksRef.current = [];
          recorderRef.current = null;

          // If cancel() was called, skip transcription entirely
          if (currentAbort.signal.aborted) {
            return;
          }

          // Push-to-talk: an accidental tap (too short) OR an empty capture is NOT a
          // transcription — surface the friendly hint and return to idle.
          if (shortReleaseRef.current || blob.size === 0) {
            setState('idle');
            setHint(PTT_HINT);
            return;
          }

          setState('transcribing');

          transcribeRef
            .current(blob, {
              language: languageRef.current,
              filename: 'recording.webm',
              signal: currentAbort.signal,
            })
            .then((result: AgentTranscribeResponseDto) => {
              onTranscriptRef.current(result.text);
              setState('idle');
              setError(null);
            })
            .catch((err: unknown) => {
              if (err instanceof Error && err.name === 'AbortError') return;
              setState('error');
              setError('Transkription fehlgeschlagen');
            });
        };

        recorder.start();
        setState('recording');
      })
      .catch((err: unknown) => {
        const name = err instanceof Error ? (err as { name?: string }).name ?? '' : '';
        if (name === 'NotAllowedError' || name === 'SecurityError') {
          setState('denied');
          setError('Mikrofonzugriff verweigert');
        } else {
          setState('error');
          setError('Kein Mikrofon gefunden');
        }
      });
  }, [setState, stopTracks]);

  // ── stop (pointer-UP / leave / cancel-pointer) ──────────────────────────────────

  const stop = useCallback(() => {
    const current = stateRef.current;

    // Released before getUserMedia resolved → treat as a tap: hint, no transcription.
    // The pending getUserMedia bails via the `!== 'requesting'` guard (state now idle).
    if (current === 'requesting') {
      setState('idle');
      setHint(PTT_HINT);
      return;
    }

    // Active recording → measure the hold; a too-short press is flagged for onstop.
    if (current === 'recording') {
      const heldMs = Date.now() - pressStartRef.current;
      shortReleaseRef.current = heldMs < minHoldRef.current;
      recorderRef.current?.stop();
      return;
    }

    // idle / denied / error / transcribing / unsupported → nothing to stop.
  }, [setState]);

  // ── Unmount cleanup ───────────────────────────────────────────────────────────

  useEffect(() => {
    return () => {
      // Abort any in-flight transcribe (signals onstop to skip)
      abortRef.current?.abort();
      abortRef.current = null;
      // Stop recorder — onstop sees signal.aborted = true, skips transcription
      const rec = recorderRef.current;
      if (rec && rec.state === 'recording') {
        rec.stop();
      }
      // Release OS mic indicator
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
      chunksRef.current = [];
      recorderRef.current = null;
      // Note: no setState here — component is unmounting
  };
  }, []);

  return {
    state,
    supported: supportedRef.current,
    error,
    hint,
    start,
    stop,
    cancel,
  };
}
